import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { MdCheckCircle, MdLocationOff, MdSearch } from 'react-icons/md';
import { LocacaoMonitoravel, listarLocacoesMonitoramento } from '../api';
import ModalLocalizarEndereco from './modal-localizar-endereco';

interface Props {
  idEmpresa: string;
  /** "Todas as locações ativas": só mostra quem está sem endereço localizado. */
  todas: boolean;
  /** Seleção ao abrir o formulário; daí em diante a lista guarda a própria seleção e avisa por onChange. */
  selecionadasIniciais: string[];
  onChange: (ids: string[]) => void;
  disabled?: boolean;
}

const cor = { borda: '#e0e5ee', texto: '#1f2937', suave: '#64748b', azul: '#1F467E', verde: '#15803d', ambar: '#b45309' };

/**
 * Escolha das locações do Monitoramento de Endereço. O endereço que vale é o do locatário; se ele ainda não foi
 * localizado no mapa, a localização é feita num modal próprio (busca pelo texto e pino no mapa). O ponto
 * confirmado fica no cadastro do locatário e serve para as próximas locações dele.
 */
const LocacoesMonitoramentoPicker: React.FC<Props> = ({ idEmpresa, todas, selecionadasIniciais, onChange, disabled }) => {
  const [selecionadas, setSelecionadas] = useState<string[]>(selecionadasIniciais);
  const [locacoes, setLocacoes] = useState<LocacaoMonitoravel[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState('');
  const [localizando, setLocalizando] = useState<LocacaoMonitoravel | null>(null);

  useEffect(() => {
    setCarregando(true);
    listarLocacoesMonitoramento(idEmpresa)
      .then(setLocacoes)
      .catch(() => toast.error('Não foi possível carregar as locações ativas.'))
      .finally(() => setCarregando(false));
  }, [idEmpresa]);

  const semEndereco = useMemo(() => locacoes.filter(l => !l.localizado), [locacoes]);

  const visiveis = useMemo(() => {
    const base = todas ? semEndereco : locacoes;
    const termo = busca.trim().toLowerCase();
    if (!termo) return base;
    return base.filter(l => `${l.nomeLocatario} ${l.placa}`.toLowerCase().includes(termo));
  }, [todas, semEndereco, locacoes, busca]);

  const alternar = useCallback((l: LocacaoMonitoravel) => {
    if (disabled) return;
    const marcada = selecionadas.includes(l.idLocacao);
    const novas = marcada ? selecionadas.filter(id => id !== l.idLocacao) : [...selecionadas, l.idLocacao];
    setSelecionadas(novas);
    onChange(novas);
    // Marcou uma locação sem endereço no mapa: abre a localização na hora.
    if (!marcada && !l.localizado) setLocalizando(l);
  }, [disabled, selecionadas, onChange]);

  const pontoSalvo = useCallback((idLocatario: string, lat: number, lng: number) => {
    setLocacoes(atuais => atuais.map(l => (l.idLocatario === idLocatario
      ? { ...l, latitude: lat, longitude: lng, localizado: true }
      : l)));
    setLocalizando(null);
  }, []);

  const linha = (l: LocacaoMonitoravel) => {
    const marcada = selecionadas.includes(l.idLocacao);
    return (
      <div key={l.idLocacao} style={{ display: 'flex', alignItems: 'center', gap: 10, borderBottom: `1px solid ${cor.borda}`, padding: '10px 4px' }}>
        {!todas && (
          <input
            type="checkbox"
            checked={marcada}
            disabled={disabled}
            onChange={() => alternar(l)}
            aria-label={`Monitorar ${l.nomeLocatario} ${l.placa}`}
          />
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: cor.texto }}>
            {l.nomeLocatario} <span style={{ fontWeight: 400, color: cor.suave }}>· {l.placa}</span>
          </div>
          <div style={{ fontSize: 12.5, color: cor.suave }}>{l.endereco || 'Sem endereço no cadastro'}</div>
        </div>
        {l.localizado ? (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, whiteSpace: 'nowrap' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: cor.verde }}>
              <MdCheckCircle size={15} /> localizado
            </span>
            {!disabled && (
              <button
                type="button"
                onClick={() => setLocalizando(l)}
                style={{ background: 'none', border: 'none', padding: 0, color: cor.azul, cursor: 'pointer', textDecoration: 'underline' }}
              >
                ajustar
              </button>
            )}
          </span>
        ) : (
          <button
            type="button"
            disabled={disabled}
            onClick={() => setLocalizando(l)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: cor.ambar, whiteSpace: 'nowrap',
              background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, padding: '4px 8px', cursor: 'pointer',
            }}
          >
            <MdLocationOff size={15} /> Localizar endereço
          </button>
        )}
      </div>
    );
  };

  if (carregando) return <div style={{ fontSize: 13, color: cor.suave }}>Carregando locações ativas...</div>;

  if (locacoes.length === 0)
    return <div style={{ fontSize: 13, color: cor.suave }}>Nenhuma locação ativa no momento.</div>;

  return (
    <div>
      {todas ? (
        <div style={{ fontSize: 13, color: cor.texto, marginBottom: 6 }}>
          {semEndereco.length === 0
            ? `Todas as ${locacoes.length} locações ativas serão monitoradas, e as novas entram sozinhas.`
            : `${locacoes.length - semEndereco.length} de ${locacoes.length} locações ativas serão monitoradas, e as novas entram sozinhas. Localize o endereço destas para incluí-las:`}
        </div>
      ) : (
        <div style={{ position: 'relative', marginBottom: 6 }}>
          <MdSearch size={16} color={cor.suave} style={{ position: 'absolute', left: 10, top: 10 }} />
          <input
            className="form-control"
            placeholder="Buscar locação por locatário ou placa..."
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            style={{ paddingLeft: 32 }}
          />
          <div style={{ fontSize: 12, color: cor.suave, marginTop: 4 }}>
            {selecionadas.length === 0 ? 'Nenhuma locação escolhida.' : `${selecionadas.length} ${selecionadas.length === 1 ? 'locação escolhida' : 'locações escolhidas'}.`}
          </div>
        </div>
      )}
      <div style={{ maxHeight: 360, overflowY: 'auto', border: `1px solid ${cor.borda}`, borderRadius: 8, padding: '0 8px' }}>
        {visiveis.map(linha)}
        {visiveis.length === 0 && (
          <div style={{ fontSize: 13, color: cor.suave, padding: 12 }}>
            {todas ? 'Nenhuma locação sem endereço.' : 'Nenhuma locação encontrada.'}
          </div>
        )}
      </div>

      <ModalLocalizarEndereco
        locacao={localizando}
        idEmpresa={idEmpresa}
        onClose={() => setLocalizando(null)}
        onSalvo={pontoSalvo}
      />
    </div>
  );
};

export default LocacoesMonitoramentoPicker;
